import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/scrollbar";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { Keyboard, Scrollbar, Navigation, Pagination } from "swiper/modules";

// Imagen del carousel con fade-in suave. Cada slide reserva una altura fija para
// que la imagen (que llega de R2 con un pequeño retraso) no "empuje" el layout al
// cargar; el fade evita el pop-in brusco. `loaded` se reinicia cuando cambia el
// `src` (placeholder transparente -> render real) para que el fade se dispare con
// la imagen definitiva.
const CarouselImage = ({ src, alt }) => {
  // Se guarda QUÉ src terminó de cargar: el placeholder transparente también
  // "carga", y antes eso dejaba la opacity en 1 y el render real aparecía de golpe.
  const [loadedSrc, setLoadedSrc] = useState(null);
  const imgRef = useRef(null);
  const esPlaceholder = !src || src.startsWith("data:");
  // Si la imagen ya estaba en caché, su onLoad pudo dispararse antes de este
  // efecto: se marca en el siguiente frame para que el fade sí se vea.
  useEffect(() => {
    const img = imgRef.current;
    if (esPlaceholder || !(img && img.complete && img.naturalWidth > 0)) return;
    const id = requestAnimationFrame(() => setLoadedSrc(src));
    return () => cancelAnimationFrame(id);
  }, [src, esPlaceholder]);
  const loaded = !esPlaceholder && loadedSrc === src;
  return (
    <div className="flex items-end justify-center h-[300px] md:h-[380px] w-full overflow-hidden">
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={() => !esPlaceholder && setLoadedSrc(src)}
        className={`max-w-[370px] max-h-full w-auto h-auto object-contain transition-all duration-1000 ease-out ${
          loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
        }`}
      />
    </div>
  );
};

const Carousal = ({ products, block, textBlock }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className="md:pt-[150px]">
      
      {/* Mobile ONLY: Artículo ARRIBA del carousel (siempre visible) */}
      {isMobile && block && (
        <div className="px-6 pb-10 pt-20">
          <article className="text-offBlack">
            <header className="mb-[9px]">
              <p className="mb-2.5 text-sm leading-7">{block.miniTitle}</p>
              <h2 className="text-2xl leading-snug mb-5">{block.title}</h2>
            </header>
            <div>
              <p className="text-base leading-7">{block.para}</p>
            </div>
          </article>
        </div>
      )}

      <div>
        <Swiper
          slidesPerView={4}
          centeredSlides={false}
          slidesPerGroupSkip={1}
          grabCursor={true}
          keyboard={{
            enabled: true,
          }}
          breakpoints={{
            1600: {
              slidesPerView: 4,
              spaceBetween: 30,
              slidesPerGroup: 1,
            },
            1440: {
              slidesPerView: 3,
              slidesPerGroup: 1,
            },
            1200: {
              slidesPerView: 3,
              spaceBetween: 30,
              slidesPerGroup: 1,
            },
            769: {
              slidesPerView: 3,
              spaceBetween: 20,
              slidesPerGroup: 1,
            },
            0: {
              slidesPerView: 1,
              spaceBetween: 15,
              slidesPerGroup: 1,
            },
          }}
          scrollbar={true}
          navigation={true}
          modules={[Keyboard, Scrollbar, Navigation, Pagination]}
          className="productCarousel"
        >
          {/* Desktop ONLY: Primer slide es el artículo */}
          {!isMobile && block && (
            <SwiperSlide>
              <aside className="h-full flex items-end pb-16">
                <article className="text-offBlack pr-4 pl-20">
                  <header className="mb-[9px]">
                    <p className="mb-2.5 text-sm leading-7">{block.miniTitle}</p>
                    <h2 className="text-3xl leading-snug mb-5">{block.title}</h2>
                  </header>
                  <div>
                    <p className="text-sm leading-7">{block.para}</p>
                    <div className="mt-[30px]">
                      <div className="w-btn">
                        <a className="flex items-center" href={block.link || "/"}>
                          {block.btn}
                          <span className="pl-4">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                              />
                            </svg>
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </article>
              </aside>
            </SwiperSlide>
          )}

          {/* Slides de productos */}
          {products.map((item, index) => {
            const productUrl = item.handle ? `/products/${item.handle}` : "/";
            return (
              <SwiperSlide key={index}>
                <Link to={productUrl} className="group flex flex-col-reverse items-center">
                  <div className="text-center text-offBlack">
                    <h5 className="font-medium my-2.5 leading-7 text-sm underline-offset-4 group-hover:underline">
                      {item.name}
                    </h5>
                    <div className="text-sm font-light">{item.desc}</div>
                  </div>
                  <CarouselImage src={item.img} alt={item.name} />
                </Link>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
};

export default Carousal;